import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  FolderKanban,
  PlusCircle,
  Search,
  Filter,
  CheckCircle2,
  Clock,
  Star,
  Trash2,
  RotateCcw,
  ExternalLink,
  ChevronUp,
  ChevronDown,
  Edit,
  Eye,
  EyeOff,
  Layers,
  Sparkles,
} from "lucide-react";
import { projectsService } from "@/api/projectsService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";

/**
 * ============================================================
 * ADMIN PROJECTS MANAGEMENT (/admin/projects)
 * ============================================================
 * Complete portfolio management center.
 * Implements listing, search, filtering, display reordering,
 * instant publish/featured toggles, soft deletion, and restoration.
 */
export default function AdminProjects() {
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [publicationFilter, setPublicationFilter] = useState("all"); // 'all' | 'published' | 'draft' | 'trash'

  const fetchCatalog = async () => {
    try {
      setLoading(true);
      const [projs, cats] = await Promise.all([
        projectsService.getAllProjectsAdmin({
          published: publicationFilter,
          category: selectedCategory,
          status: selectedStatus,
          search: searchTerm,
          includeDeleted: publicationFilter === "trash",
        }),
        projectsService.getCategories(),
      ]);
      setProjects(projs);
      setCategories(cats);
    } catch (err) {
      console.error("[AdminProjects] Fetch error:", err);
      toast.error(err.message || "Failed to load projects.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicationFilter, selectedCategory, selectedStatus]);

  // Handle live search
  const filteredProjects = useMemo(() => {
    if (!searchTerm.trim()) return projects;
    const q = searchTerm.toLowerCase().trim();
    return projects.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        p.slug.toLowerCase().includes(q) ||
        p.client.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.stack.some((s) => s.toLowerCase().includes(q))
    );
  }, [projects, searchTerm]);

  // Quick action: Toggle Publish
  const handleTogglePublish = async (project) => {
    try {
      const nextState = !project.is_published;
      await projectsService.togglePublish(project.id, nextState);
      toast.success(`Project "${project.title}" ${nextState ? "published" : "moved to drafts"}.`);
      setProjects((prev) =>
        prev.map((p) => (p.id === project.id ? { ...p, is_published: nextState } : p))
      );
    } catch (err) {
      toast.error(err.message || "Failed to update publication status.");
    }
  };

  // Quick action: Toggle Featured
  const handleToggleFeatured = async (project) => {
    try {
      const nextState = !project.is_featured;
      await projectsService.toggleFeatured(project.id, nextState);
      toast.success(`Project "${project.title}" ${nextState ? "marked as featured" : "unfeatured"}.`);
      setProjects((prev) =>
        prev.map((p) => (p.id === project.id ? { ...p, is_featured: nextState } : p))
      );
    } catch (err) {
      toast.error(err.message || "Failed to update featured status.");
    }
  };

  // Quick action: Soft delete
  const handleSoftDelete = async (project) => {
    if (!window.confirm(`Move project "${project.title}" to trash? It will be hidden from the public website.`)) return;
    try {
      await projectsService.softDeleteProject(project.id);
      toast.success(`Project "${project.title}" moved to trash.`);
      fetchCatalog();
    } catch (err) {
      toast.error(err.message || "Failed to delete project.");
    }
  };

  // Quick action: Restore
  const handleRestore = async (project) => {
    try {
      await projectsService.restoreProject(project.id);
      toast.success(`Project "${project.title}" restored.`);
      fetchCatalog();
    } catch (err) {
      toast.error(err.message || "Failed to restore project.");
    }
  };

  // Quick action: Hard delete
  const handlePermanentDelete = async (project) => {
    if (!window.confirm(`PERMANENT ACTION: Delete project "${project.title}" and all its storage media? This cannot be undone.`)) return;
    try {
      await projectsService.hardDeleteProject(project.id);
      toast.success(`Project "${project.title}" permanently removed.`);
      fetchCatalog();
    } catch (err) {
      toast.error(err.message || "Failed to delete project.");
    }
  };

  // Quick action: Reorder (Move Up / Move Down)
  const handleMoveOrder = async (index, direction) => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= filteredProjects.length) return;

    const copy = [...filteredProjects];
    const temp = copy[index];
    copy[index] = copy[targetIndex];
    copy[targetIndex] = temp;

    // Optimistically update UI
    setProjects(copy);

    try {
      await projectsService.reorderProjects(copy);
      toast.success("Project display ordering updated.");
    } catch (err) {
      toast.error(err.message || "Failed to reorder projects.");
      fetchCatalog();
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Banner & Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2.5">
            <FolderKanban className="w-6 h-6 text-cyan-600" />
            <span>Projects & Portfolio Management</span>
          </h1>
          <p className="text-xs text-slate-600 mt-1 font-medium">
            Manage case studies, narratives, metrics, and media published across Shiveda IT Services.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <Button
            onClick={() => navigate("/admin/taxonomy")}
            variant="outline"
            className="h-10 text-xs border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 hover:text-slate-900 rounded-xl font-medium"
          >
            <Layers className="w-4 h-4 mr-1.5 text-cyan-600" />
            <span>Taxonomy</span>
          </Button>

          <Button
            onClick={() => navigate("/admin/projects/new")}
            className="h-10 px-5 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold gap-2 shadow-sm"
          >
            <PlusCircle className="w-4 h-4" />
            <span>New Project</span>
          </Button>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3 shadow-xs">
        <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
          {/* Search Box */}
          <div className="relative w-full md:w-80">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              type="text"
              placeholder="Search by title, client, or tech..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 h-10 bg-slate-50 border-slate-200 text-xs text-slate-900 placeholder:text-slate-400 rounded-xl focus:bg-white"
            />
          </div>

          {/* Filter Dropdowns */}
          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-xs text-slate-800 font-medium focus:outline-none focus:border-cyan-500"
            >
              <option value="All" className="bg-white text-slate-900">
                All Categories
              </option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id} className="bg-white text-slate-900">
                  {cat.name}
                </option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-xs text-slate-800 font-medium focus:outline-none focus:border-cyan-500"
            >
              <option value="All" className="bg-white text-slate-900">
                All Statuses
              </option>
              <option value="Ongoing" className="bg-white text-slate-900">
                Ongoing
              </option>
              <option value="Completed" className="bg-white text-slate-900">
                Completed
              </option>
            </select>

            {/* Publication Filter Tabs */}
            <div className="flex items-center bg-slate-100 border border-slate-200 rounded-xl p-1 text-xs">
              <button
                type="button"
                onClick={() => setPublicationFilter("all")}
                className={cn(
                  "px-3 py-1 rounded-lg text-xs font-medium transition-colors",
                  publicationFilter === "all"
                    ? "bg-white text-cyan-700 font-semibold shadow-xs"
                    : "text-slate-600 hover:text-slate-900"
                )}
              >
                All
              </button>
              <button
                type="button"
                onClick={() => setPublicationFilter("published")}
                className={cn(
                  "px-3 py-1 rounded-lg text-xs font-medium transition-colors",
                  publicationFilter === "published"
                    ? "bg-white text-emerald-700 font-semibold shadow-xs"
                    : "text-slate-600 hover:text-slate-900"
                )}
              >
                Published
              </button>
              <button
                type="button"
                onClick={() => setPublicationFilter("draft")}
                className={cn(
                  "px-3 py-1 rounded-lg text-xs font-medium transition-colors",
                  publicationFilter === "draft"
                    ? "bg-white text-amber-700 font-semibold shadow-xs"
                    : "text-slate-600 hover:text-slate-900"
                )}
              >
                Drafts
              </button>
              <button
                type="button"
                onClick={() => setPublicationFilter("trash")}
                className={cn(
                  "px-3 py-1 rounded-lg text-xs font-medium transition-colors",
                  publicationFilter === "trash"
                    ? "bg-white text-rose-700 font-semibold shadow-xs"
                    : "text-slate-600 hover:text-slate-900"
                )}
              >
                Trash
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Projects Table View */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-xs">
        {loading ? (
          <div className="py-16 text-center text-xs text-slate-400 font-medium">Loading projects catalog...</div>
        ) : filteredProjects.length === 0 ? (
          <div className="py-16 text-center text-xs text-slate-400 font-medium space-y-2">
            <div>No projects found matching your criteria.</div>
            <Button
              onClick={() => {
                setSearchTerm("");
                setSelectedCategory("All");
                setSelectedStatus("All");
                setPublicationFilter("all");
              }}
              variant="link"
              className="text-cyan-600 text-xs font-semibold"
            >
              Reset Filters
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-slate-200 bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider font-semibold">
                <tr>
                  <th className="py-3.5 px-4 w-16 text-center">Order</th>
                  <th className="py-3.5 px-4">Project</th>
                  <th className="py-3.5 px-4">Category</th>
                  <th className="py-3.5 px-4">Status</th>
                  <th className="py-3.5 px-4 text-center">Visibility</th>
                  <th className="py-3.5 px-4 text-center">Featured</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredProjects.map((project, idx) => (
                  <tr
                    key={project.id}
                    className={cn(
                      "hover:bg-slate-50/80 transition-colors group",
                      project.deleted_at && "opacity-60 bg-rose-50/30"
                    )}
                  >
                    {/* Order Column */}
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <span className="font-mono text-slate-500 w-5 text-center font-medium">
                          {project.display_order}
                        </span>
                        {publicationFilter !== "trash" && (
                          <div className="flex flex-col">
                            <button
                              type="button"
                              onClick={() => handleMoveOrder(idx, "up")}
                              disabled={idx === 0}
                              className="text-slate-400 hover:text-cyan-600 disabled:opacity-20 transition-colors"
                              title="Move Up"
                            >
                              <ChevronUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMoveOrder(idx, "down")}
                              disabled={idx === filteredProjects.length - 1}
                              className="text-slate-400 hover:text-cyan-600 disabled:opacity-20 transition-colors"
                              title="Move Down"
                            >
                              <ChevronDown className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Project Title & Client */}
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        {project.thumbnail ? (
                          <img
                            src={project.thumbnail}
                            alt={project.title}
                            className="w-10 h-10 rounded-lg object-cover border border-slate-200 shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-cyan-600 shrink-0">
                            <FolderKanban className="w-5 h-5" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 truncate max-w-xs flex items-center gap-1.5">
                            <span>{project.title}</span>
                            {project.live_url && (
                              <a
                                href={project.live_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-slate-400 hover:text-cyan-600 transition-colors"
                                title="Visit Live Site"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5 font-medium">
                            <span className="font-mono text-slate-500">{project.slug}</span>
                            {project.client && (
                              <>
                                <span>•</span>
                                <span>{project.client}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Category */}
                    <td className="py-3 px-4">
                      <Badge className="bg-slate-100 text-slate-700 border-slate-200 text-[10px] font-medium">
                        {project.category}
                      </Badge>
                    </td>

                    {/* Project Status */}
                    <td className="py-3 px-4">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-semibold",
                          project.status === "Completed"
                            ? "bg-blue-50 text-blue-700 border border-blue-200"
                            : "bg-amber-50 text-amber-700 border border-amber-200"
                        )}
                      >
                        {project.status === "Completed" ? (
                          <CheckCircle2 className="w-3 h-3" />
                        ) : (
                          <Clock className="w-3 h-3" />
                        )}
                        <span>{project.status}</span>
                      </span>
                    </td>

                    {/* Visibility Switch */}
                    <td className="py-3 px-4 text-center">
                      {project.deleted_at ? (
                        <Badge className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">
                          In Trash
                        </Badge>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleTogglePublish(project)}
                          className={cn(
                            "inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-colors",
                            project.is_published
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                              : "bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200"
                          )}
                          title="Click to toggle publish status"
                        >
                          {project.is_published ? (
                            <>
                              <Eye className="w-3 h-3" />
                              <span>Live</span>
                            </>
                          ) : (
                            <>
                              <EyeOff className="w-3 h-3" />
                              <span>Draft</span>
                            </>
                          )}
                        </button>
                      )}
                    </td>

                    {/* Featured Switch */}
                    <td className="py-3 px-4 text-center">
                      <button
                        type="button"
                        onClick={() => handleToggleFeatured(project)}
                        disabled={Boolean(project.deleted_at)}
                        className={cn(
                          "p-1.5 rounded-lg transition-colors",
                          project.is_featured
                            ? "text-amber-600 bg-amber-50 hover:bg-amber-100 border border-amber-200"
                            : "text-slate-300 hover:text-slate-600"
                        )}
                        title="Toggle Featured on Home Page"
                      >
                        <Star className={cn("w-4 h-4", project.is_featured && "fill-amber-500 text-amber-500")} />
                      </button>
                    </td>

                    {/* Actions */}
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {project.deleted_at ? (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRestore(project)}
                              className="h-8 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50 rounded-lg gap-1 font-medium"
                              title="Restore to Active"
                            >
                              <RotateCcw className="w-3 h-3" />
                              <span>Restore</span>
                            </Button>

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePermanentDelete(project)}
                              className="h-8 text-xs text-rose-600 hover:bg-rose-50 rounded-lg font-medium"
                              title="Delete Permanently"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Link
                              to={`/projects/${project.slug}`}
                              target="_blank"
                              className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-800 hover:bg-slate-100 transition-colors"
                              title="Preview on Website"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </Link>

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/admin/projects/${project.slug || project.id}/edit`)}
                              className="h-8 text-xs text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-lg gap-1 font-medium"
                            >
                              <Edit className="w-3.5 h-3.5 text-cyan-600" />
                              <span>Edit</span>
                            </Button>

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSoftDelete(project)}
                              className="h-8 w-8 p-0 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg"
                              title="Move to Trash"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
